import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, RefreshCw } from 'lucide-react';
import {
  getMovementsByJob,
  markMovementAsUsed,
  markJobMovementsAsUsed
} from '../../redux/api';

import {
  getWithdrawnMovementLabel,
  getWithdrawnMovementSourceLabel,
  WITHDRAWN_STATUS_LABELS,
} from '../../utils/withdrawnItemsUtils';

const STATUS_CLASSES = {
  RESERVED: 'bg-yellow-900 text-yellow-300',
  ISSUED: 'bg-green-900 text-green-300',
  RETURNED: 'bg-blue-900 text-blue-300',
  USED: 'bg-purple-900 text-purple-300',
  CANCELLED: 'bg-gray-700 text-gray-300'
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(value || 0);

const JobMaterialsPanel = ({
  jobId,
  showActions = true,
  onUpdated,
  compact = false
}) => {
  const { t } = useTranslation();
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);

  const fetchMovements = useCallback(async () => {
    if (!jobId) return;
    try {
      setLoading(true);
      const response = await getMovementsByJob(jobId);
      setMovements(response.data || []);
    } catch (error) {
      console.error('Error fetching job materials:', error);
      setMovements([]);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  const handleMarkUsed = async (movementId) => {
    try {
      setActionId(movementId);
      await markMovementAsUsed(movementId);
      await fetchMovements();
      onUpdated?.();
    } catch (error) {
      console.error('Error marking movement as used:', error);
    } finally {
      setActionId(null);
    }
  };

  const handleMarkAllUsed = async () => {
    try {
      setActionId('all');
      await markJobMovementsAsUsed(jobId);
      await fetchMovements();
      onUpdated?.();
    } catch (error) {
      console.error('Error marking all movements as used:', error);
    } finally {
      setActionId(null);
    }
  };

  const issuedCount = movements.filter((m) => m.status === 'ISSUED').length;
  const totalCost = movements
    .filter((m) => ['ISSUED', 'USED'].includes(m.status))
    .reduce((sum, m) => sum + (m.totalCost || 0), 0);

  if (!jobId) return null;

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-gray-300">
          <Package className="h-4 w-4" />
          <span>
            {t('inventory.withdrawn.jobMaterialsAll', 'Povučena roba za posao')}
          </span>
          {!loading && movements.length > 0 && (
            <span className="text-gray-500">
              ({movements.length}) · {formatCurrency(totalCost)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {showActions && issuedCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllUsed}
              disabled={actionId === 'all'}
              className="text-xs text-purple-400 hover:text-purple-300 disabled:opacity-50"
            >
              {t('inventory.withdrawn.markAllUsed', 'Označi sve kao iskorišćeno')}
            </button>
          )}
          <button
            type="button"
            onClick={fetchMovements}
            className="text-gray-400 hover:text-white"
            title={t('common.refresh', 'Osveži')}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && movements.length === 0 ? (
        <p className="text-xs text-gray-500">{t('common.loading', 'Učitavanje...')}</p>
      ) : movements.length === 0 ? (
        <p className="text-xs text-gray-500">
          {t('inventory.withdrawn.noJobMaterials', 'Nema povučene robe ni delova za ovaj posao')}
        </p>
      ) : (
        <div className="space-y-2">
          {movements.map((movement) => (
            <div
              key={movement._id}
              className="flex flex-wrap items-center justify-between gap-2 rounded border border-gray-700 bg-gray-900/50 px-3 py-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium text-white truncate">
                  {getWithdrawnMovementLabel(movement)}
                </div>
                <div className="text-xs text-gray-400">
                  {getWithdrawnMovementSourceLabel(movement)}
                  {' · '}{movement.quantity} {movement.unit}
                  {movement.workerId && (
                    <> · {movement.workerId.firstName} {movement.workerId.lastName}</>
                  )}
                  {' · '}{formatCurrency(movement.totalCost)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-xs ${STATUS_CLASSES[movement.status] || 'bg-gray-700 text-gray-300'}`}>
                  {WITHDRAWN_STATUS_LABELS[movement.status] || movement.status}
                </span>
                {showActions && movement.status === 'ISSUED' && (
                  <button
                    type="button"
                    onClick={() => handleMarkUsed(movement._id)}
                    disabled={actionId === movement._id}
                    className="text-xs text-purple-400 hover:text-purple-300 disabled:opacity-50"
                  >
                    {t('inventory.withdrawn.used', 'Iskorišćeno')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobMaterialsPanel;
